import { FormControlLabel, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import Autocomplete from "components/Autocomplete";
import StyledCheckBox from "components/Checkbox";
import { theme } from "config/theme";
import useText from "constants/text";
import { isVehicleTruckChecked, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { EVehiclesInput, EVehiculeType } from "generated/graphql";
import React, { useCallback, useMemo } from "react";
import { CamionType } from "../shared/CamionType";
import { ContainerRadioElement, DescriptionRadio, ErrorStep, TitleRadio } from "../shared/MainStyles";
import { isFieldWithValue } from "../shared/utils";
import VehicleCounter from "../shared/VehicleCounter";

export interface StylesPropsVehicle {
  width?: string;
  marginLeft?: string;
}
const useStyles = makeStyles<Theme, StylesPropsVehicle>({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: { width: "auto" },
  },
  formStyle: {
    [theme.breakpoints.down("xs")]: {
      flexBasis: "auto",
      maxWidth: "100%",
    },
  },
  items: {
    margin: "30px 0",
    [theme.breakpoints.down("xs")]: {
      paddingRight: 50,
    },
  },
  checbox: {
    marginRight: "30px",
    [theme.breakpoints.down("xs")]: {
      marginRight: "0",
      marginLeft: 10,
      marginTop: -60,
    },
  },
  select: {
    [theme.breakpoints.down("xs")]: {
      width: ({ width }) => (width ? width : "83%"),
      marginTop: "15px",
      paddingLeft: "80px",
      paddingBottom: "21px",
      marginLeft: ({ marginLeft }) => (marginLeft ? marginLeft : ""),
    },
  },
});

export default function VehicleType() {
  const { requestQuote, setRequestQuote, setDetails, fieldError, truckType, requestQuoteNT, setRequestQuoteNT } =
    React.useContext(NewRequestQuoteContext);
  const { text } = useText();
  const styles = useStyles({ width: "100%", marginLeft: "-50px" });


  const vehicleTypes = [
    {
      type: EVehiculeType.Scooter,
      title: "SCOOTER",
      desc: text.requestQuote.desc_vehicle_1,
      enableCounter: true,
    },
    {
      type: EVehiculeType.MiniVan,
      title: "MINIVAN",
      desc: text.requestQuote.desc_vehicle_2,
      enableCounter: false,
    },
    {
      type: EVehiculeType.Van,
      title: "VAN",
      desc: text.requestQuote.desc_vehicle_3,
      enableCounter: false,
    },
    {
      type: "TRUCK",
      title: text.requestQuote.truck,
      desc: text.requestQuote.desc_vehicle_4,
      enableCounter: true,
    },
  ];
  const handleChange = (vehicleType: string, description?: string) => {
    setDetails(description);
    const vehicles = requestQuote?.vehicles || requestQuoteNT?.vehicles || [];
    const selectedIndex = vehicles?.findIndex((v) => v?.vehicleType === vehicleType);
    let newSelected: Array<EVehiclesInput> = [];
    if (selectedIndex === -1) {
      newSelected = vehicles?.concat({ vehicleType: vehicleType as EVehiculeType, numberOfVehicule: 1 });
    } else if (selectedIndex >= 0) {
      newSelected = vehicles?.slice(0, selectedIndex).concat(vehicles?.slice(selectedIndex + 1));
    }
    setRequestQuote({ ...requestQuote, vehicles: newSelected });
    setRequestQuoteNT({ ...requestQuoteNT, vehicles: newSelected });
  };

  const onChangeVehicleNumber = (vehicleType: string, value: number) => {
    const vehicles =
      requestQuote?.vehicles?.map((item) =>
        item.vehicleType === vehicleType
          ? {
            vehicleType,
            numberOfVehicule: value,
          }
          : item
      ) ||
      requestQuoteNT?.vehicles?.map((item) =>
        item.vehicleType === vehicleType
          ? {
            vehicleType,
            numberOfVehicule: value,
          }
          : item
      );
    setRequestQuote({ ...requestQuote, vehicles });
    setRequestQuoteNT({ ...requestQuoteNT, vehicles });
  };

  const vehicleError = useMemo(
    () => (fieldError && !requestQuote?.vehicles?.length) || !requestQuoteNT?.vehicles?.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldError, requestQuote, requestQuoteNT, truckType]
  );

  const isTruckChecked = useCallback(
    () => isVehicleTruckChecked(requestQuote?.vehicles || requestQuoteNT?.vehicles),
    [requestQuote?.vehicles, requestQuoteNT?.vehicles]
  );

  const truckTypeError = useMemo(
    () => fieldError && isTruckChecked() && !isFieldWithValue(truckType),
    [fieldError, requestQuote, requestQuoteNT, truckType]
  );

  const isChecked = useCallback(
    (vehicleType: string) => {
      if (requestQuote?.vehicles?.length) {
        return (
          requestQuote?.vehicles?.findIndex((v) => v.vehicleType === vehicleType) !== -1
        );
      }
      if (requestQuoteNT?.vehicles?.length) {
        return (
          requestQuoteNT?.vehicles?.findIndex((v) => v.vehicleType === vehicleType) !== -1
        );
      }
      return false;
    },
    [requestQuote?.vehicles, requestQuoteNT?.vehicles]
  );

  const getVehicleTypeCounter = useCallback(
    (vehicleType: string) => {
      if (requestQuote?.vehicles?.length || requestQuoteNT?.vehicles?.length) {
        return (
          requestQuote?.vehicles?.filter((v) => v.vehicleType === vehicleType)[0]?.numberOfVehicule ||
          requestQuoteNT?.vehicles?.filter((v) => v.vehicleType === vehicleType)[0]?.numberOfVehicule ||
          0
        );
      }
      return 0;
    },
    [requestQuote?.vehicles, requestQuoteNT?.vehicles]
  );

  return (
    <NewRequestQuoteContext.Consumer>
      {({ truckType, setTruckType }) => (
        <>
          <Grid container className={styles.root}>
            <Grid item xs={6} className={styles.formStyle}>
              {vehicleTypes.map((vehicle) => (
                <Grid item xs={12} key={vehicle.title} className={styles.items}>
                  <FormControlLabel
                    value={vehicle.type}
                    control={
                      <StyledCheckBox
                        className={styles.checbox}
                        checked={isChecked(vehicle?.type)}
                        name={vehicle.type}
                        onChange={() => handleChange(vehicle.type, vehicle.desc)}
                        value={isChecked(vehicle?.type)}
                      />
                    }
                    label={
                      <>
                        <ContainerRadioElement>
                          <TitleRadio>{vehicle.title}</TitleRadio>
                          {isChecked(vehicle.type) && (
                            <VehicleCounter
                              countValue={getVehicleTypeCounter(vehicle.type)}
                              onChangeCountValue={(value) => onChangeVehicleNumber(vehicle.type, value)}
                            />
                          )}
                        </ContainerRadioElement>
                        <Typography
                          variant="body2"
                          css={`
                            letter-spacing: normal;
                            line-height: 1.3;
                          `}
                        >
                          <DescriptionRadio>{vehicle.desc}</DescriptionRadio>
                        </Typography>
                      </>
                    }
                    css={`
                      align-items: flex-start;
                    `}
                  />
                </Grid>
              ))}
              <Grid item xs={11} className={styles.select}>
                <Autocomplete
                  label={text.requestQuote.select_truck_type}
                  options={CamionType}
                  getOptionLabel={(option: any) => option?.replace(/_/g, " ")}
                  getOptionSelected={(option: any, value: any) => option === value}
                  fullWidth
                  onChange={(event: any, value: any) => {
                    setTruckType(value);
                  }}
                  value={truckType}
                  error={truckTypeError}
                  helperText={truckTypeError ? text.requestQuote.truck_type_error : ""}
                  disabled={!isTruckChecked()}
                />
              </Grid>
            </Grid>
          </Grid>
          {vehicleError && <ErrorStep>{text.requestQuote.vehicle_type_error}</ErrorStep>}
        </>
      )}
    </NewRequestQuoteContext.Consumer>
  );
}
